import { useState, useEffect, useCallback } from 'react';
import {
  getMeBenefitsV1,
  getMeBenefitsAutocompleteV1,
} from '../../api/me-—-profile/me-—-profile';
import { GetMeBenefitsV1Params } from '../../api/model';

type BenefitsResponse = Awaited<ReturnType<typeof getMeBenefitsV1>>;
type AutocompleteResponse = Awaited<ReturnType<typeof getMeBenefitsAutocompleteV1>>;

export function useBenefits(params?: GetMeBenefitsV1Params, locale?: string) {
  const [data, setData] = useState<BenefitsResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const paramsKey = JSON.stringify(params);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getMeBenefitsV1(
        params,
        locale ? { headers: { 'Accept-Language': locale } } : undefined
      );
      setData(response);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, locale]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, refetch };
}

export function useBenefitsSearch(query: string, locale?: string) {
  const [data, setData] = useState<BenefitsResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (query.length < 2) {
      setData(undefined);
      return;
    }
    setIsLoading(true);
    getMeBenefitsV1(
      { Query: query },
      locale ? { headers: { 'Accept-Language': locale } } : undefined
    )
      .then(response => setData(response))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [query, locale]);

  return { data, isLoading };
}

export function useBenefitsAutocomplete(query: string, locale?: string) {
  const [data, setData] = useState<AutocompleteResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (query.length < 2) {
      setData(undefined);
      return;
    }
    setIsLoading(true);
    getMeBenefitsAutocompleteV1(
      { query, take: 8 },
      locale ? { headers: { 'Accept-Language': locale } } : undefined
    )
      .then(response => setData(response))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [query, locale]);

  return { data, isLoading };
}
